import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminSessionsNav from './AdminSessionsNav';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminUsers.css';
import './AdminSessions.css';

const AUTO_REFRESH_INTERVAL_MS = 15000;

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function severityClassName(severity) {
  switch (severity) {
    case 'fatal':
      return 'cq-admin-badge cq-admin-badge--error';
    case 'error':
      return 'cq-admin-badge cq-admin-badge--disabled';
    case 'warn':
      return 'cq-admin-badge cq-admin-badge--warn';
    default:
      return 'cq-admin-badge cq-admin-badge--neutral';
  }
}

const AdminSessionErrorsPage = () => {
  const [filters, setFilters] = useState({
    environment: '',
    severity: '',
    layer: '',
    has_session: '',
    category: '',
    range: '7d',
    page: 1,
  });
  const [expandedErrorId, setExpandedErrorId] = useState('');
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    errors: [],
    pagination: {
      page: 1,
      total_pages: 1,
      total_count: 0,
    },
    lastUpdatedAt: null,
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({ intervalMs: AUTO_REFRESH_INTERVAL_MS });

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const controller = new AbortController();

    const loadErrors = async ({ background = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const response = await fetch('/.netlify/functions/admin-get-errors', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...filters,
            page_size: 25,
          }),
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load errors.');
        }

        setState({
          loading: false,
          refreshing: false,
          error: '',
          errors: data.errors || [],
          pagination: data.pagination || {
            page: 1,
            total_pages: 1,
            total_count: 0,
          },
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load errors.',
          errors: [],
          pagination: {
            page: 1,
            total_pages: 1,
            total_count: 0,
          },
          lastUpdatedAt: null,
        });
      }
    };

    loadErrors();

    return () => {
      controller.abort();
    };
  }, [filters, refreshNonce]);

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Session Monitoring</p>
          <h2 className="cq-admin-page__title">Global Errors</h2>
          <p className="cq-admin-page__copy">System-wide error visibility, including events not attached to a specific game session.</p>
        </div>
        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row">
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      <AdminSessionsNav />

      <section className="cq-admin-panel cq-admin-session-filters-grid">
        <label className="cq-admin-field">
          <span>Environment</span>
          <select className="cq-admin-input" value={filters.environment} onChange={(event) => setFilters((current) => ({ ...current, environment: event.target.value, page: 1 }))}>
            <option value="">All</option>
            <option value="development">Development</option>
            <option value="staging">Staging</option>
            <option value="production">Production</option>
            <option value="unknown">Unknown</option>
          </select>
        </label>
        <label className="cq-admin-field">
          <span>Severity</span>
          <select className="cq-admin-input" value={filters.severity} onChange={(event) => setFilters((current) => ({ ...current, severity: event.target.value, page: 1 }))}>
            <option value="">All</option>
            <option value="info">Info</option>
            <option value="warn">Warn</option>
            <option value="error">Error</option>
            <option value="fatal">Fatal</option>
          </select>
        </label>
        <label className="cq-admin-field">
          <span>Layer</span>
          <select className="cq-admin-input" value={filters.layer} onChange={(event) => setFilters((current) => ({ ...current, layer: event.target.value, page: 1 }))}>
            <option value="">All</option>
            <option value="frontend">Frontend</option>
            <option value="netlify">Netlify</option>
            <option value="supabase_function">Supabase Function</option>
            <option value="db">DB</option>
            <option value="external_api">External API</option>
          </select>
        </label>
        <label className="cq-admin-field">
          <span>Has Session</span>
          <select className="cq-admin-input" value={filters.has_session} onChange={(event) => setFilters((current) => ({ ...current, has_session: event.target.value, page: 1 }))}>
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>
        <label className="cq-admin-field">
          <span>Category</span>
          <input className="cq-admin-input" value={filters.category} onChange={(event) => setFilters((current) => ({ ...current, category: event.target.value, page: 1 }))} />
        </label>
        <label className="cq-admin-field">
          <span>Time Range</span>
          <select className="cq-admin-input" value={filters.range} onChange={(event) => setFilters((current) => ({ ...current, range: event.target.value, page: 1 }))}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </label>
      </section>

      {state.loading ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">Loading errors...</p></div> : null}
      {!state.loading && state.error ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">{state.error}</p></div> : null}
      {!state.loading && !state.error && !state.errors.length ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">No errors found.</p></div> : null}

      {!state.loading && !state.error && state.errors.length ? (
        <>
          <section className="cq-admin-panel">
            <div className="cq-admin-table-wrap">
              <table className="cq-admin-table">
                <thead>
                  <tr>
                    <th>Occurred At</th>
                    <th>Severity</th>
                    <th>Environment</th>
                    <th>Layer</th>
                    <th>Category</th>
                    <th>Message</th>
                    <th>Session</th>
                    <th>User</th>
                  </tr>
                </thead>
                <tbody>
                  {state.errors.map((error) => {
                    const expanded = expandedErrorId === error.id;

                    return (
                      <React.Fragment key={error.id}>
                        <tr className="cq-admin-session-row-clickable" onClick={() => setExpandedErrorId(expanded ? '' : error.id)}>
                          <td>{formatDateTime(error.occurred_at)}</td>
                          <td><span className={severityClassName(error.severity)}>{error.severity}</span></td>
                          <td>{error.environment}</td>
                          <td>{error.layer}</td>
                          <td>{error.category || 'Not available'}</td>
                          <td>{error.message}</td>
                          <td>
                            {error.game_session_id ? (
                              <Link className="cq-admin-card-link cq-admin-cell-id" to={`/admin/sessions/${error.game_session_id}`}>{error.game_session_id}</Link>
                            ) : 'None'}
                          </td>
                          <td className="cq-admin-cell-id">{error.user_id || 'None'}</td>
                        </tr>
                        {expanded ? (
                          <tr>
                            <td colSpan={8}>
                              <div className="cq-admin-session-error-detail">
                                <dl className="cq-admin-definition-list">
                                  <div><dt>Function Name</dt><dd>{error.function_name || 'Not available'}</dd></div>
                                  <div><dt>Route</dt><dd>{error.route || 'Not available'}</dd></div>
                                  <div><dt>Provider</dt><dd>{error.provider || 'Not available'}</dd></div>
                                  <div><dt>Request ID</dt><dd>{error.request_id || 'Not available'}</dd></div>
                                  <div><dt>HTTP Status</dt><dd>{error.http_status || 'Not available'}</dd></div>
                                  <div><dt>Conversation ID</dt><dd className="cq-admin-cell-id">{error.conversation_id || 'Not available'}</dd></div>
                                </dl>
                                <div className="cq-admin-session-error-block">
                                  <h4>Stack Trace</h4>
                                  <pre className="cq-admin-pre">{error.stack || 'Not available'}</pre>
                                </div>
                                <div className="cq-admin-session-error-block">
                                  <h4>Metadata</h4>
                                  <pre className="cq-admin-pre">{JSON.stringify(error.metadata || {}, null, 2)}</pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <div className="cq-admin-session-pagination">
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.pagination.page <= 1}
              onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
            >
              Previous
            </button>
            <span>Page {state.pagination.page} of {state.pagination.total_pages}</span>
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.pagination.page >= state.pagination.total_pages}
              onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
            >
              Next
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default AdminSessionErrorsPage;
